/**
 * Composition helpers — the styled-components replacement pattern. Each
 * returns a `React.CSSProperties` object built from tokens, to be spread
 * into inline styles: `style={{ ...panelSurface(), padding: SPACE.lg }}`.
 *
 * Pure functions over pure data — zero runtime CSS injection, no provider,
 * jsdom-safe.
 */
import type { CSSProperties } from 'react';
import { COLORS, ACCENT, INK, MOOD } from './colors';
import { RADII } from './spacing';
import { SHADOW } from './elevation';
import { FONTS, FONT_SIZE, type FontSizeKey } from './typography';

/** Standard dark panel surface (border + radius + shadow). */
export function panelSurface(): CSSProperties {
  return {
    background: COLORS.surface,
    border: `1px solid ${COLORS.border}`,
    borderRadius: RADII.lg,
    boxShadow: SHADOW.md,
  };
}

/**
 * Ink-&-brass panel base (HUD-restyle §2) — the top-lit INK gradient body + a
 * 1px brass fitting border + soft shadow. The shared substrate for the quiet
 * always-on HUD panels (quest tracker, quick menu, buff bar). The flagship
 * player frame layers its brass edge-line + low-HP state on top of this same
 * material; kept as one helper so the family reads as one system (no
 * per-component palettes, §7). NOT for dev/diagnostic surfaces (§7).
 *
 * `moodTint` (from useHudMood, §6) layers a clamped ±6% warm/cold wash over the
 * ink BASE only — never text or bars. Neutral/omitted → transparent (no-op).
 */
export function inkPanel(moodTint: string = MOOD.neutral): CSSProperties {
  return {
    background: `linear-gradient(${moodTint}, ${moodTint}), linear-gradient(180deg, ${INK.hi}, ${INK.base} 55%, ${INK.lo})`,
    border: `1px solid ${ACCENT.brass}`,
    borderRadius: RADII.lg,
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.45), inset 0 1px 0 rgba(255, 255, 255, 0.05)',
  };
}

export type TextTone = 'primary' | 'secondary' | 'muted' | 'accent';

/** Text colour + size from tokens. */
export function textStyle(tone: TextTone = 'primary', size: FontSizeKey = 'md'): CSSProperties {
  const color =
    tone === 'primary' ? COLORS.textPrimary :
    tone === 'secondary' ? COLORS.textSecondary :
    tone === 'muted' ? COLORS.textMuted :
    COLORS.textAccent;
  return { color, fontSize: FONT_SIZE[size] };
}

export type StatusTone = 'ok' | 'warn' | 'bad' | 'neutral';

/** Map a status tone to its semantic colour. */
export function statusColor(tone: StatusTone): string {
  switch (tone) {
    case 'ok': return COLORS.success;
    case 'warn': return COLORS.warning;
    case 'bad': return COLORS.danger;
    default: return COLORS.textMuted;
  }
}

/** Monospace text style for debug/diagnostic readouts. */
export function monoText(size: FontSizeKey = 'sm'): CSSProperties {
  return { fontFamily: FONTS.mono, fontSize: FONT_SIZE[size] };
}

/**
 * Capitalize the first letter, lowercase the rest. The single idiom behind
 * the per-component `formatClassName` / inline-capitalize one-liners
 * (PlayerList / PlayerInfoPanel / PartyPanel / resolveUrlClassCharacter).
 * Generic — works on any label (class name, job name, …), not class-specific.
 */
export function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

/**
 * Faint diagonal accent tint behind a stat/card — a hex color at ~13% alpha
 * fading to transparent. The shared idiom behind the Character/Stats panels
 * (previously copy-pasted as `linear-gradient(135deg, ${color}20, transparent)`).
 * Expects a `#rrggbb` color (hex-alpha suffix).
 */
export function accentTint(hexColor: string): string {
  return `linear-gradient(135deg, ${hexColor}20, transparent)`;
}
